import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useAuthModal } from "@/hooks/use-auth-modal";
import { SubscriptionPurchaseButton } from "./SubscriptionPurchaseButton";
import { motion, AnimatePresence } from "framer-motion";

// Detect Safari for alternative styling (blur causes GPU texture eviction)
const isSafari = typeof navigator !== 'undefined' && 
  (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
   (/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream));

const HERO_IMAGE_URL = 'https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/hero-banners/New%20Authbackground/AUTHBACKGROUND%20copy.webp';

export function FreeTrialCard() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const { openAuthModal } = useAuthModal();

  const handleClick = () => {
    if (!session) {
      openAuthModal('subscription_box');
    } else if (subscriptionStatus?.subscription_status === 'cancelled') {
      navigate('/v2/profile');
    } else if (subscriptionStatus?.subscription_status !== 'active') {
      navigate('/v2/profile');
    }
  };

  const daysLeft = subscriptionStatus?.subscription_status === 'cancelled' && subscriptionStatus?.current_period_end
    ? Math.ceil((new Date(subscriptionStatus.current_period_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="relative w-full min-h-[400px] md:min-h-[500px]">
      {/* Ambient glow background - Safari uses gradient to avoid GPU texture eviction */}
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          className="absolute inset-0 z-0 pointer-events-none rounded-2xl overflow-hidden"
        >
          {isSafari ? (
            // Safari: static radial gradient instead of blur
            <div 
              className="absolute inset-0 opacity-70"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(180,60,60,0.5) 0%, rgba(30,34,57,0.4) 50%, transparent 80%)'
              }}
            />
          ) : (
            // Chrome/Firefox: full blur effect
            <div
              className="absolute inset-0 scale-150 blur-[80px] opacity-85 saturate-150"
              style={{
                backgroundImage: `url(${HERO_IMAGE_URL})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/30" />
        </motion.div>
      </AnimatePresence>

      {/* Hero Card - matching NetflixHero style */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative w-full overflow-hidden shadow-2xl rounded-2xl border border-white/20 z-10 cursor-pointer transition-all duration-200 active:scale-[0.98]"
        style={{ 
          height: '60vh', 
          minHeight: '400px', 
          maxHeight: '550px',
        }}
        onClick={handleClick}
      >
        {/* Glow effect behind cover */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HERO_IMAGE_URL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(60px) saturate(1.5)',
            opacity: 0.8,
            transform: 'scale(1.2)',
          }}
        />
        
        {/* Cover Image */}
        <img
          src={HERO_IMAGE_URL}
          alt="BuxBox"
          className="absolute inset-0 w-full h-full object-cover z-[1]"
        />
        
        {/* BuxBox Logo Badge */}
        <div className="absolute top-4 left-4 z-10">
          <div className="w-10 h-10 rounded-lg bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/10">
            <img 
              src="/logo.png" 
              alt="BuxBox" 
              className="w-6 h-6 object-contain"
            />
          </div>
        </div>
        
        {/* Bottom grey blur with fade */}
        <div 
          className="absolute inset-x-0 bottom-0 h-56 backdrop-blur-xl z-[2]"
          style={{ 
            maskImage: 'linear-gradient(to top, black 0%, black 40%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to top, black 0%, black 40%, transparent 100%)',
            backgroundColor: 'rgba(255,255,255,0.05)'
          }}
        />
        
        {/* Bottom gradient overlay */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent z-[3]" />
        
        {/* Content overlay - center aligned */}
        <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2 text-center z-[4]">
          {/* Logo */}
          <div className="flex justify-center mb-2">
            <img 
              alt="BuxBox Logo" 
              className="h-12 w-12 rounded-xl" 
              width="48" 
              height="48" 
              src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/New%20Busbox%20logo/Assets.xcassets/AppIcon.appiconset/buxbox-gradient-icon-optimized.webp" 
            />
          </div>
          
          {/* Text */}
          <p className="font-apple font-semibold text-white text-sm leading-relaxed max-w-sm mx-auto">
            Enjoy new favourites every week.
          </p>
          {daysLeft > 0 && (
            <p className="text-xs text-gray-400 font-apple">
              You have only {daysLeft} day{daysLeft === 1 ? '' : 's'} left
            </p>
          )}
          
          {/* Button */}
          <div className="pt-2 max-w-xs mx-auto" onClick={e => e.stopPropagation()}>
            <SubscriptionPurchaseButton />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
